import type { ExplorerFolderTreeNode } from '@/components/layout/explorer-inline-folder-node'
import { normalizeExplorerPath } from '@/lib/explorer-tree-focus'

const CACHE_PREFIX = 'flownotes_explorer_tree_v1'

type CachedTreePayload = {
  structureRevision: number
  nodes: ExplorerFolderTreeNode[]
  savedAtMs: number
}

function cacheKey(treeKey: string): string {
  return `${CACHE_PREFIX}:${treeKey}`
}

export function readExplorerTreeCache(
  treeKey: string,
  structureRevision: number
): ExplorerFolderTreeNode[] | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(treeKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedTreePayload
    if (parsed.structureRevision !== structureRevision) return null
    if (!Array.isArray(parsed.nodes)) return null
    return parsed.nodes
  } catch {
    return null
  }
}

export function writeExplorerTreeCache(
  treeKey: string,
  structureRevision: number,
  nodes: ExplorerFolderTreeNode[]
): void {
  try {
    const payload: CachedTreePayload = {
      structureRevision,
      nodes,
      savedAtMs: Date.now(),
    }
    sessionStorage.setItem(cacheKey(treeKey), JSON.stringify(payload))
  } catch {
    /* quota or private mode */
  }
}

/** True if a folder or file node with this path exists anywhere in the loaded tree. */
export function treeContainsFolderPath(
  nodes: ExplorerFolderTreeNode[],
  folderPath: string
): boolean {
  const target = normalizeExplorerPath(folderPath)
  if (!target) return false

  const walk = (list: ExplorerFolderTreeNode[]): boolean => {
    for (const node of list) {
      if (normalizeExplorerPath(node.path) === target) return true
      if (node.children?.length && walk(node.children)) return true
    }
    return false
  }
  return walk(nodes)
}

export function clearExplorerTreeCache(treeKey?: string): void {
  try {
    if (treeKey) {
      sessionStorage.removeItem(cacheKey(treeKey))
      return
    }
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i)
      if (k?.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k)
    }
  } catch {
    /* ignore */
  }
}
